import dayjs from "dayjs";
import PropTypes from "prop-types";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import { yupResolver } from "@hookform/resolvers/yup";
import { Divider, Paper, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";

import { useAuth } from "@/features/authentication";
import { OutletRoleEnum } from "@/features/outlets";
import { computeOrderTotals } from "@/features/shared";
import { AddProductVariantSection, CenteredLoading } from "@/features/ui";
import { GeneralInformationSection } from "./general-information-section";
import { OtherSection } from "./other-section";
import { PriceSummarySection } from "./price-summary-section";
import { ProductVariantsSection } from "./product-variants-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./purchase-order-form-constants";

export const PurchaseOrderForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  editMode = false,
  isLoading = false,
}) => {
  const { outlet } = useAuth();
  const methods = useForm({
    defaultValues: defaultValues
      ? { ...DEFAULT_VALUES, ...defaultValues }
      : DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const { control, handleSubmit, formState } = methods;

  const [buyer, seller, sellerableType] = useWatch({
    control,
    name: ["buyer", "seller", "sellerableType"],
  });

  // Browse the catalogue as whoever has to be able to sell the product. On an
  // internal purchase the seller is a HUB outlet and the backend rejects lines
  // it can't access, so listing as the buyer offers products that fail on
  // submit. An external supplier has no product scope — fall back to the buyer.
  const productOutletId =
    sellerableType === "OUTLET" ? seller?.id : buyer?.id;

  const { append } = useFieldArray({
    control,
    name: "purchaseOrderProductVariants",
  });

  const onFormSubmit = async (formData) => {
    const { taxAmount, grandTotal } = computeOrderTotals(formData);

    const payload = {
      taxable: formData.taxable,
      totalIncludesTax: formData.taxable && formData.totalIncludesTax,
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      warehouseId: formData.warehouse.id,
      buyerId: formData.buyer.id,
      ...(!editMode && {
        sellerableId: formData.seller.id,
        sellerableType: formData.sellerableType,
      }),
      paymentTermId: formData.paymentTerm.id,
      note: formData.note || null,
      taxAmount,
      taxPercentage: formData.taxPercentage,
      discountType: formData.discountType,
      discountAmount: formData.discountInput || 0,
      grandTotal,
      purchaseOrderProductVariants: formData.purchaseOrderProductVariants.map(
        (variant) => ({
          ...(editMode && variant.id ? { id: variant.id } : {}),
          sku: variant.sku,
          quantity: variant.quantity,
          discount: variant.discount || 0,
          costPerUnit: variant.costPerUnit || 0,
          note: variant.note || null,
        })
      ),
    };

    await onSubmit(payload);
  };

  return isLoading ? (
    <CenteredLoading sx={{ height: "100vh" }} />
  ) : (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        spacing={2.5}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection editMode={editMode} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack spacing={2.5}>
              <AddProductVariantSection
                outletId={productOutletId}
                onAdded={(productVariant, product, quantity) => {
                  // Use distributorPrice for RETAIL outlets, 0 for HUB
                  const costPerUnit =
                    outlet?.role === OutletRoleEnum.RETAIL
                      ? productVariant.distributorPrice || 0
                      : 0;

                  append({
                    id: ulid(),
                    productVariantId: productVariant.id,
                    name: productVariant.name,
                    sku: productVariant.sku,
                    unitOfMeasurement: product.unitOfMeasurement.name,
                    costPerUnit,
                    discount: 0,
                    quantity,
                    receivedQuantity: 0,
                  });
                }}
              />
              <Divider />
              <ProductVariantsSection />
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 5, lg: 7 }}>
          <OtherSection />
        </Grid>
        <Grid size={{ xs: 12, md: 7, lg: 5 }}>
          <PriceSummarySection />
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

PurchaseOrderForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  renderActionButton: PropTypes.func,
  editMode: PropTypes.bool,
  isLoading: PropTypes.bool,
};
